(() => {
  if (window.__KamokDepoV3Loaded) return;
  window.__KamokDepoV3Loaded = true;

  const PRODUCT_LIMIT = 5;
  const initialized = new WeakSet();

  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'lt-LT', {
        style: 'currency',
        currency,
      }).format(value);
    } catch {
      return value.toFixed(2) + ' €';
    }
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  }[char]));

  function centsFrom(value, source) {
    const number = Number(String(value ?? '').replace(',', '.'));
    if (!Number.isFinite(number)) return 0;
    return source === 'task' ? Math.round(number * 100) : Math.round(number);
  }

  function imageUrl(product) {
    const image = product?.featured_image?.url || product?.featured_image || product?.image || product?.images?.[0];
    return typeof image === 'string' ? image : image?.src || image?.url || '';
  }

  function productUrl(product) {
    return product?.url || '/products/' + (product?.handle || '');
  }

  function fromStatic(node, index) {
    return {
      id: String(node.dataset.kamokDepoProduct || ''),
      variantId: String(node.dataset.variantId || ''),
      title: node.dataset.productTitle || node.querySelector('.kamok-depo__title')?.textContent?.trim() || '',
      type: node.dataset.productType || '',
      handle: node.dataset.productHandle || '',
      url: node.querySelector('.kamok-depo__title')?.getAttribute('href') || '',
      image: node.querySelector('img')?.currentSrc || node.querySelector('img')?.src || '',
      priceCents: Number(node.dataset.priceCents || 0),
      quickAdd: node.dataset.quickAdd === '1' && Boolean(node.dataset.variantId),
      available: true,
      source: 'manual',
      order: index,
    };
  }

  function fromProduct(product, source, index) {
    const variants = product?.variants || [];
    const variant = variants.find((item) => item.available !== false) || variants[0];
    if (!variant) return null;
    const priceCents = centsFrom(variant.price ?? product.price_min ?? product.price, source);
    if (!priceCents) return null;
    return {
      id: String(product.id || ''),
      variantId: String(variant.id || ''),
      title: product.title || '',
      type: product.type || product.product_type || '',
      handle: product.handle || '',
      url: productUrl(product),
      image: imageUrl(product),
      priceCents,
      quickAdd: variants.length === 1 && variant.available !== false,
      available: product.available !== false && variant.available !== false,
      source,
      order: index,
    };
  }

  function uniqueCandidates(items, anchorId, excludedIds) {
    const seen = new Set();
    return items.filter((item) => {
      if (!item?.id || !item.available) return false;
      if (item.id === anchorId || excludedIds.has(item.id) || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  function taskTerms(root) {
    const text = (root.dataset.productTitle + ' ' + root.dataset.productType + ' ' + root.dataset.productVendor).toLowerCase();

    const isDog = /(šun|sunu|šuns|suns|dog)/.test(text);
    const isCat = /(kat|kačių|kaciu|cat)/.test(text);
    if (isDog || isCat) {
      const brand = /royal canin/.test(text) ? 'Royal Canin ' : '';
      const stage = /(puppy|šuniuk|suniuk)/.test(text)
        ? 'Puppy '
        : /(kitten|kačiuk|kaciuk)/.test(text)
          ? 'Kitten '
          : /(adult|suaug)/.test(text)
            ? 'Adult '
            : /(senior|mature)/.test(text)
              ? 'Senior '
              : '';
      const species = isDog ? 'šunų ' : 'kačių ';
      if (/(saus|dry)/.test(text)) {
        return [
          brand + stage + species + 'drėgnas maistas',
          brand + stage + species + 'konservai'
        ];
      }
      if (/(drėgn|dregn|wet|konserv)/.test(text)) {
        return [brand + stage + species + 'sausas maistas'];
      }
      return [
        brand + stage + species + 'drėgnas maistas',
        brand + stage + species + 'sausas maistas'
      ];
    }

    if (/(židin|zidin|kamin|kepsnin|gril|kamado)/.test(text)) return ['degtukai', 'grilio valymo'];
    if (/(srov|elektr|jungikl|relė|rele|gnybt|kabel|kištuk|kistuk|lizd|wago|abb|brennenstuhl)/.test(text)) return ['izoliacinė juosta', 'Wago 2273'];
    if (/(keptuv|virtuv|indų|indu |indaplov|puod|stalo|maisto)/.test(text)) return ['kempinė', 'mikropluosto šluostė'];
    if (/(džiovykl|dziovykl)/.test(text)) return ['džiovyklės priedai', 'džiovyklės kamuoliukai'];
    if (/(valikl|valym|dezinf|tualet|wc|švar|svar)/.test(text)) return ['mikropluosto šluostė', 'kempinė'];
    if (/(grąžt|grazt|pjūkl|pjukl|makita|dewalt|skil|įrank|irank)/.test(text)) return ['apsauginiai akiniai', 'darbo pirštinės'];
    if (/(automobil|stiklų|stiklu|auto )/.test(text)) return ['mikropluosto šluostė', 'stiklų valiklis'];
    return [];
  }

  async function fetchTask(root) {
    const terms = taskTerms(root);
    if (!terms.length) return [];
    const groups = await Promise.all(terms.map(async (term, groupIndex) => {
      try {
        const url = rootUrl() + 'search/suggest.json?q=' + encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide';
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) return [];
        const payload = await response.json();
        return (payload?.resources?.results?.products || [])
          .map((product, index) => fromProduct(product, 'task', groupIndex * 20 + index))
          .filter(Boolean);
      } catch {
        return [];
      }
    }));
    return groups.flat();
  }

  // Native Shopify Search & Discovery complements mapped accessories.
  // Only per-cart-item roots use this additional request.
  async function fetchNativeComplementary(root) {
    const productId = String(root.dataset.productId || '');
    if (!productId) return [];
    try {
      const url = rootUrl() + 'recommendations/products.json?product_id=' +
        encodeURIComponent(productId) + '&limit=6&intent=complementary';
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return [];
      const payload = await response.json();
      return (payload.products || []).map((product,index) =>
        fromProduct(product,'recommendation',index)
      ).filter(Boolean);
    } catch {
      return [];
    }
  }

  async function readCart() {
    try {
      const response = await fetch(rootUrl() + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return { items: [] };
      return response.json();
    } catch {
      return { items: [] };
    }
  }

  function cartProductIds(cart) {
    return new Set((cart?.items || []).map((item) => String(item.product_id || '')).filter(Boolean));
  }

  function chooseDiverse(candidates, limit = PRODUCT_LIMIT) {
    const selected = [];
    const usedTypes = new Set();

    for (const item of candidates) {
      const type = String(item.type || '').trim().toLowerCase();
      if (type && !usedTypes.has(type)) {
        selected.push(item);
        usedTypes.add(type);
      }
      if (selected.length >= limit) return selected;
    }

    for (const item of candidates) {
      if (selected.some((selectedItem) => selectedItem.id === item.id)) continue;
      selected.push(item);
      if (selected.length >= limit) break;
    }
    return selected;
  }

  function renderCard(item) {
    const article = document.createElement('article');
    article.className = 'kamok-depo__card';
    article.dataset.kamokDepoProduct = item.id;
    article.dataset.variantId = item.variantId || '';
    article.dataset.priceCents = String(item.priceCents || 0);
    article.dataset.productType = item.type || '';
    article.dataset.productTitle = item.title || '';
    article.dataset.productHandle = item.handle || '';
    article.dataset.quickAdd = item.quickAdd ? '1' : '0';

    const image = item.image
      ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">'
      : '';
    const action = item.quickAdd
      ? '<button type="button" class="button button--secondary kamok-depo__button" data-kamok-depo-add data-variant-id="' + escapeHtml(item.variantId) + '">+ Pridėti</button>'
      : '<a class="button button--secondary kamok-depo__button" href="' + escapeHtml(item.url) + '">Pasirinkti</a>';

    article.innerHTML =
      '<a class="kamok-depo__media" href="' + escapeHtml(item.url) + '" aria-label="' + escapeHtml(item.title) + '">' + image + '</a>' +
      '<a class="kamok-depo__title" href="' + escapeHtml(item.url) + '">' + escapeHtml(item.title) + '</a>' +
      '<span class="kamok-depo__price">' + money(item.priceCents) + '</span>' + action;

    return article;
  }

  function renderRail(root, items, context) {
    const list = root.querySelector('[data-kamok-depo-list]');
    const smart = root.querySelector('[data-kamok-depo-smart]');
    const head = root.querySelector('[data-kamok-depo-heading]');
    const sub = root.querySelector('[data-kamok-depo-subheading]');
    if (!list) return;

    if (smart) {
      smart.hidden = true;
      smart.innerHTML = '';
    }
    list.hidden = false;
    list.replaceChildren(...items.map(renderCard));

    if (context === 'cart') {
      const perItem = root.dataset.kamokPerItem === 'true';
      if (head) head.textContent = perItem ? 'Tinka kartu' : 'Dar gali prireikti';
      if (sub) sub.textContent = perItem ? 'Šiai prekei tinkami priedai' : 'Atrinkta pagal krepšelį';
    } else {
      if (head) head.textContent = 'Tinka kartu';
      if (sub) sub.textContent = 'priedai ir naudingi papildymai';
    }

    const section = root.closest('.km-product-smart-rec');
    const empty = section?.querySelector('[data-kamok-depo-empty]');
    root.hidden = items.length === 0;
    if (section) section.hidden = false;
    if (empty) empty.hidden = items.length > 0;
  }

  async function hydrate(root) {
    if (initialized.has(root)) return;
    initialized.add(root);

    const list = root.querySelector('[data-kamok-depo-list]');
    if (!list) return;

    const context = root.dataset.context || 'product';
    const staticCandidates = [...list.querySelectorAll('[data-kamok-depo-product]')].map(fromStatic);
    root.dataset.loading = '1';

    try {
      const cart = await readCart();
      const excluded = context === 'cart' ? cartProductIds(cart) : new Set();
      const anchorId = String(root.dataset.productId || '');
      const nativeCandidates = root.dataset.kamokPerItem === 'true'
        ? await fetchNativeComplementary(root) : [];
      const taskCandidates = (staticCandidates.length + nativeCandidates.length >= PRODUCT_LIMIT)
        ? [] : await fetchTask(root);

      // DEPO is complementary only. Related/alternative products belong to Commerce Brain.
      const candidates = uniqueCandidates(
        [...staticCandidates, ...nativeCandidates, ...taskCandidates],
        anchorId,
        excluded
      );

      renderRail(root, chooseDiverse(candidates, PRODUCT_LIMIT), context);
    } catch (error) {
      console.warn('[KamOk DEPO] hydrate failed', error);
      if (!list.children.length) {
        root.hidden = true;
        const section = root.closest('.km-product-smart-rec');
        const empty = section?.querySelector('[data-kamok-depo-empty]');
        if (section) section.hidden = false;
        if (empty) empty.hidden = false;
      }
    } finally {
      root.dataset.loading = '0';
      root.dataset.ready = '1';
    }
  }

  async function addVariant(button, variantId) {
    if (!variantId || button.disabled) return;
    button.disabled = true;
    const old = button.textContent;
    button.textContent = 'Pridedama…';

    try {
      const response = await fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error('cart add ' + response.status);

      if (typeof window.refreshDawnCartUI === 'function') await window.refreshDawnCartUI();
      window.dispatchEvent(new CustomEvent('kamok:cart-updated'));

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      button.textContent = '✓ Pridėta';
    } catch (error) {
      console.warn('[KamOk DEPO] add failed', error);
      button.textContent = 'Bandyti dar';
      button.disabled = false;
      return;
    }

    setTimeout(() => {
      if (button.isConnected) {
        button.textContent = old;
        button.disabled = false;
      }
    }, 900);
  }

  function scan(scope = document) {
    if (scope.matches?.('[data-kamok-depo]')) hydrate(scope);
    scope.querySelectorAll?.('[data-kamok-depo]').forEach(hydrate);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-kamok-depo-add]');
    if (!button) return;
    event.preventDefault();
    addVariant(button, button.dataset.variantId);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  window.addEventListener('kamok:cart-updated', () => scan(document));

  const bindNativeCart = () => {
    if (window.__kamokDepoNativeCartBound) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;
    window.__kamokDepoNativeCartBound = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => scan(document));
  };
  bindNativeCart();
  document.addEventListener('DOMContentLoaded', bindNativeCart, { once:true });

  // Dawn can replace cart HTML without executing newly injected inline scripts.
  // Only react to newly inserted root blocks, not to recommendation cards.
  let refreshTimer = 0;
  new MutationObserver(records => {
    const inserted = records.some(record => [...record.addedNodes].some(node =>
      node.nodeType === 1 && (
        node.matches?.('[data-kamok-depo],.kamok-cart-crosssell') ||
        node.querySelector?.('[data-kamok-depo]')
      )
    ));
    if (!inserted) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => scan(document), 80);
  }).observe(document.documentElement, { childList:true, subtree:true });
})();
